import { deepEqual } from '@benzinga/utils';
import { DataField, DataFieldIngress, FilterObject, FullDataField } from './entities';
import quoteMapping from './quote-mapping.json';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { WatchlistManager } from '@benzinga/watchlist-manager';

interface QuotesFieldsDefsUpdateEvent {
  type: 'quotes_fields:update';
  fields: FullDataField[];
}

export type QuoteFieldsStoreEvent = QuotesFieldsDefsUpdateEvent;

export class QuoteFieldsStore extends ListenableSubscribable<QuoteFieldsStoreEvent> {
  private ingressFields?: DataFieldIngress;
  private scannersDataFields?: FullDataField[];
  private scannersDataFieldsByName?: Map<string, FullDataField>;
  private filterableDataFields?: FullDataField[];
  private columnableDataFields?: FullDataField[];
  private numericColumnableDataFields?: FullDataField[];
  private fieldIds = new Set<string>();

  constructor() {
    super();
    quoteMapping.forEach(q => this.fieldIds.add(q.name));
  }

  public shouldWeFetchFields = (): boolean => {
    if (this.scannersDataFields === undefined) {
      return true;
    }
    return false;
  };

  public getFieldIds = (): Set<string> => this.fieldIds;

  public getFields = (): FullDataField[] | undefined => this.scannersDataFields;
  public getFilterableFields = (): FullDataField[] | undefined => this.filterableDataFields;
  public getColumableFields = (): FullDataField[] | undefined => this.columnableDataFields;
  public getNumericColumableDefs = (): FullDataField[] | undefined => this.numericColumnableDataFields;

  public getScannerDefFromId = (name: string): FullDataField | undefined => this.scannersDataFieldsByName?.get(name);

  public updateScannersDefs = (scannersDefs: DataFieldIngress): boolean => {
    if (this.ingressFields && deepEqual(this.ingressFields, scannersDefs)) {
      return false;
    } else {
      this.ingressFields = scannersDefs;
      const quoteFields = new Set<string>(quoteMapping.map(q => q.name));

      const filterByName = new Map(Object.entries(scannersDefs.quoteFieldOptions)?.map(([k, v]) => [k, v]));

      this.scannersDataFields =
        scannersDefs.quoteFields
          ?.filter(df => quoteFields.has(df.name))
          .map<DataField>(df => {
            const filterOptions = filterByName.get(df.options ?? df.name);
            return { ...df, filterOptions } as DataField;
          }) ?? [];

      this.scannersDataFields = [...this.scannersDataFields, ...localField];
      this.filterableDataFields = this.scannersDataFields
        ?.filter(dataField => dataField.filterable)
        .map(f => f as DataField);

      this.columnableDataFields = this.scannersDataFields.filter(dataField => dataField.columnable);

      this.numericColumnableDataFields = this.columnableDataFields.filter(
        d => !['symbol', 'text', 'date', 'dateTime', 'sparkLine'].includes(d.format),
      );
      this.scannersDataFieldsByName = new Map(
        this.scannersDataFields?.filter(f => f.name).map(f => [f.name ?? '', f]) ?? [],
      );

      this.dispatch({
        fields: this.scannersDataFields ?? [],
        type: 'quotes_fields:update',
      });
      return true;
    }
  };
}

const localField: FullDataField[] = [
  {
    category: 'Reference',
    columnable: false,
    description: 'Filter for Symbols in selected watchlists.',
    filterConvert: (filter: FilterObject, session: Session) => {
      const symbols = new Set<string>();
      filter.parameters
        .map(w => w.replace('watchlistId:', ''))
        .map(w => session.getManager(WatchlistManager).getStoredWatchlistById(w))
        .flatMap(watchlist => watchlist?.symbols ?? [])
        .forEach(symbol => symbols.add(symbol.symbol));
      return { field: 'symbol', operator: 'in', parameters: Array.from(symbols) };
    },
    filterable: true,
    format: 'watchlist',
    label: 'Watchlists',
    labelShort: 'Watchlists',
    name: 'watchlist',
    permissions: 'free',
    permissionsCom: 'Pro',
    updateFrequency: 'EOD',
  },
];
